import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { useUiPreferences } from "../../context/UiPreferencesContext";

function UserFormModal({
  formState,
  setFormState,
  onClose,
  onSubmit,
  isSubmitting,
}) {
  if (!formState) return null;

  const title =
    formState.mode === "create" ? "Adăugare utilizator nou" : "Editare utilizator";

  return (
    <Modal
      title={title}
      onClose={onClose}
      size="lg"
      contentClassName="max-h-[85vh]"
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-xs font-semibold uppercase text-gray-600">
              Nume complet
            </label>
            <input
              type="text"
              value={formState.name}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, name: event.target.value }))
              }
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Introdu numele utilizatorului"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-semibold uppercase text-gray-600">
              Email
            </label>
            <input
              type="email"
              value={formState.email}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, email: event.target.value }))
              }
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="email@example.com"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-semibold uppercase text-gray-600">
              Telefon (opțional)
            </label>
            <input
              type="tel"
              value={formState.phone}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, phone: event.target.value }))
              }
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="07xxxxxxxx"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-semibold uppercase text-gray-600">
              Rol
            </label>
            <select
              value={formState.role}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, role: event.target.value }))
              }
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="patient">Pacient</option>
              <option value="doctor">Medic</option>
              <option value="assistant">Asistent</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-semibold uppercase text-gray-600">
            {formState.mode === "create"
              ? "Parolă inițială"
              : "Parolă nouă (opțional)"}
          </label>
          <input
            type="password"
            value={formState.password}
            onChange={(event) =>
              setFormState((prev) => ({ ...prev, password: event.target.value }))
            }
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder={
              formState.mode === "create"
                ? "Setează o parolă temporară"
                : "Lasă gol pentru a păstra parola curentă"
            }
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Anulează
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Se salvează..." : "💾 Salvează"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function UserManagement({
  users,
  onAddUser,
  onSaveUser,
  onDeleteUser,
  onClose,
}) {
  const [formState, setFormState] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { userColors, setUserColor } = useUiPreferences();

  const sortedUsers = useMemo(
    () =>
      Array.isArray(users)
        ? [...users].sort((a, b) => {
            const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
            const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
            return bTime - aTime;
          })
        : [],
    [users]
  );

  const openCreateForm = () => {
    setFormState({
      mode: "create",
      id: "",
      name: "",
      email: "",
      phone: "",
      password: "",
      role: "patient",
    });
  };

  const openEditForm = (user) => {
    setFormState({
      mode: "edit",
      id: user.id,
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      password: "",
      role: user.role || "patient",
    });
  };

  const closeForm = () => {
    setFormState(null);
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!formState) return;
    const { mode, id, name, email, phone, password, role } = formState;
    const trimmedName = (name || "").trim();
    const trimmedEmail = (email || "").trim().toLowerCase();
    const trimmedPhone = (phone || "").trim();

    if (!trimmedName) {
      toast.error("Numele este obligatoriu.");
      return;
    }
    if (!trimmedEmail) {
      toast.error("Emailul este obligatoriu.");
      return;
    }

    const payload = {
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      role,
    };

    if (mode === "edit") {
      payload.id = id;
    }

    if (mode === "create") {
      if (!password.trim()) {
        toast.error("Setează o parolă pentru noul utilizator.");
        return;
      }
      payload.password = password.trim();
    } else if (password.trim()) {
      payload.password = password.trim();
    }

    try {
      setIsSubmitting(true);
      if (mode === "create") {
        await onAddUser(payload);
        toast.success("Utilizator creat.");
      } else {
        await onSaveUser(payload);
        toast.success("Utilizator actualizat.");
      }
      closeForm();
    } catch (error) {
      const message = error?.message || "Operarea a eșuat.";
      toast.error(message);
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!userId) return;
    const confirmation = window.confirm("Sigur ștergi acest utilizator?");
    if (!confirmation) return;
    try {
      await onDeleteUser(userId);
      toast.success("Utilizator șters.");
    } catch (error) {
      toast.error(error?.message || "Nu s-a putut șterge utilizatorul.");
    }
  };

  const handleUserColorChange = async (userId, value) => {
    try {
      await setUserColor(userId, value);
    } catch (error) {
      toast.error(error?.message || "Nu s-a putut salva culoarea utilizatorului.");
    }
  };

  return (
    <>
      <Modal
        title="Gestionare utilizatori"
        onClose={onClose}
        size="xl"
        contentClassName="max-h-[90vh]"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <p className="text-xs sm:text-sm text-gray-500">
            Total utilizatori:{" "}
            <span className="font-semibold text-gray-700">
              {sortedUsers.length}
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="primary"
              size="sm"
              className="text-xs sm:text-sm"
              onClick={openCreateForm}
            >
              ➕ Adaugă utilizator
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <div className="relative h-full">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden rounded-2xl border border-gray-200 h-full">
                  <div className="max-h-[55vh] sm:max-h-[60vh] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold text-gray-700">
                            Nume
                          </th>
                          <th className="hidden md:table-cell px-3 py-2 md:px-4 md:py-3 text-left font-semibold text-gray-700">
                            Email
                          </th>
                          <th className="px-3 py-2 md:px-4 md:py-3 text-left font-semibold text-gray-700">
                            Rol
                          </th>
                          <th className="hidden md:table-cell px-3 py-2 md:px-4 md:py-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                            Creat
                          </th>
                          <th className="px-3 py-2 md:px-4 md:py-3 text-left font-semibold text-gray-700">
                            Culoare
                          </th>
                          <th className="px-3 py-2 md:px-4 md:py-3 text-right font-semibold text-gray-700">
                            Acțiuni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {sortedUsers.length === 0 && (
                          <tr>
                            <td
                              colSpan={6}
                              className="px-3 py-6 text-center text-gray-500"
                            >
                              Nu există utilizatori de afișat.
                            </td>
                          </tr>
                        )}
                        {sortedUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="px-3 py-2 md:px-4 md:py-3 align-top">
                              <div className="font-medium">
                                {user.name || "Fără nume"}
                              </div>
                              <div className="md:hidden text-[11px] text-gray-500 break-all">
                                {user.email}
                              </div>
                              <div className="md:hidden text-[10px] text-gray-400">
                                {user.createdAt
                                  ? new Date(user.createdAt).toLocaleString(
                                      "ro-RO",
                                      {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }
                                    )
                                  : "—"}
                              </div>
                            </td>
                            <td className="hidden md:table-cell px-3 py-2 md:px-4 md:py-3 break-all">
                              {user.email}
                            </td>
                            <td className="px-3 py-2 md:px-4 md:py-3 capitalize">
                              {user.role}
                            </td>
                            <td className="hidden md:table-cell px-3 py-2 md:px-4 md:py-3 text-xs text-gray-500 whitespace-nowrap">
                              {user.createdAt
                                ? new Date(user.createdAt).toLocaleString(
                                    "ro-RO",
                                    {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }
                                  )
                                  : "—"}
                            </td>
                            <td className="px-3 py-2 md:px-4 md:py-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={userColors?.[user.id] || "#3b82f6"}
                                  onChange={(e) => handleUserColorChange(user.id, e.target.value)}
                                  title="Alege culoarea utilizatorului"
                                  className="h-7 w-9 cursor-pointer rounded border border-gray-200 bg-white"
                                />
                                {userColors?.[user.id] ? (
                                  <button
                                    type="button"
                                    onClick={() => handleUserColorChange(user.id, null)}
                                    className="text-[11px] text-gray-500 hover:text-gray-700"
                                  >
                                    Reset
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-gray-400">Default</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 sm:px-4 sm:py-3 text-right">
                              <div className="flex flex-wrap justify-end gap-2">
                                <Button
                                  variant="warning"
                                  size="sm"
                                  className="text-xs sm:text-sm"
                                  onClick={() => openEditForm(user)}
                                >
                                  ✏️ Editează
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="text-xs sm:text-sm"
                                  onClick={() => handleDelete(user.id)}
                                >
                                  🗑️ Șterge
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <UserFormModal
        formState={formState}
        setFormState={setFormState}
        onClose={closeForm}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </>
  );
}
